import { useEffect } from "react";
import { ModalProps } from "../../interfaces/modal";
import { usePaymentStore } from "../../stores/payment";
import { Select } from "../ui/inputs/Select";
import {
  createPayment,
  getDifferencesCategories,
  getPaymentDifference,
  updatePayment,
  updatePaymentDifference,
} from "../../api/payments";
import { TextArea } from "../ui/inputs/TextArea";
import { useButtonStore } from "../../stores/components/button";
import { showToastError, showToastSuccess } from "../../services/toast";
import { parseAmountInput } from "../../services/function";
import { ModalButton, ModalShell } from "../ui/modals/ModalShell";

const PaymentDifference: React.FC<ModalProps> = ({ closeModal }) => {
  const {
    tabPayment,
    difference,
    formData,
    setFormData,
    differenceCategories,
    setDifferenceCategories,
    idsInvoicesSelected,
    viewOrUpdate,
    setViewOrUpdate,
    idPayment,
    resetPayment,
    updatePaymentBool,
    setUpdatePaymentBool,
    trueAmount,
  } = usePaymentStore();
  const { setCreateButton } = useButtonStore();

  // Récupération des données de l écart de réglement
  useEffect(() => {
    if (idPayment && tabPayment === "PaymentDifferences") {
      getPaymentDifference(idPayment)
        .then((res) => {
          setFormData(res.data);
        })
        .catch((err) => showToastError(err.response.data.message));
    }
  }, [difference]);

  // Récupération des catégories de différences
  useEffect(() => {
    getDifferencesCategories()
      .then((res) => {
        const formattedData = res?.data?.map((data: any) => ({
          label: data.name,
          value: data.id,
        }));
        setDifferenceCategories(formattedData);
      })
      .catch((err) => showToastError(err.response.data.message));
    }, []);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: parseInt(e.target.value) });
  };
  const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Gestion de la soumission du formulaire
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.idPaymentDifferenceCategory === "" || formData.idPaymentDifferenceCategory === null || formData.idPaymentDifferenceCategory === undefined) {
      showToastError("La catégorie de différence est requise");
      return;
    }
    const parsedAmount = parseAmountInput(formData.amount);
    if (!Number.isFinite(parsedAmount)) {
      showToastError("Montant TTC invalide");
      return;
    }
    closeModal();
    const data = {
        ...formData,
         // Ajout des montants appliqués pour chaque facture
         invoices: idsInvoicesSelected.map((invoice: any) => {
          return {
            id: invoice.id,
            amountApplied: invoice.MAEFA,
          }
        }),
      createAPaymentDifference: true,
      amount: parseFloat(parsedAmount.toFixed(2)),
    };
    if (viewOrUpdate === "") {
      createPayment(data)
        .then(() => {
          setCreateButton(false);
          setViewOrUpdate("");
          showToastSuccess("Encaissement créé avec différence");
          setUpdatePaymentBool(!updatePaymentBool);
          setFormData({});
          resetPayment();
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });
    } else {
      updatePayment(idPayment, data)
        .then(() => {
          setCreateButton(false);
          setViewOrUpdate("");
          setFormData({})
          showToastSuccess("Encaissement mis à jour");
          setUpdatePaymentBool(!updatePaymentBool);
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });
    }
  };

  // Gestion de la soumission du formulaire sans différence 
  const handleSubmitWithoutPaymentDiff = (e: React.FormEvent) => {
    e.preventDefault();
    const parsedAmount = parseAmountInput(formData.amount);
    if (!Number.isFinite(parsedAmount)) {
      showToastError("Montant TTC invalide");
      return;
    }
    closeModal();
    if (viewOrUpdate === "") {
      const data = {
        ...formData,
           // Ajout des montants appliqués pour chaque facture
           invoices: idsInvoicesSelected.map((invoice: any) => {
            return {
              id: invoice.id,
              amountApplied: invoice.MAEFA,
            }
          }),
        createAPaymentDifference: false,
        amount: parseFloat(parsedAmount.toFixed(2)),
      };
      createPayment(data)
        .then(() => {
          setCreateButton(false);
          setViewOrUpdate("");
          showToastSuccess("Encaissement créé");
          setUpdatePaymentBool(!updatePaymentBool);
          setFormData({});
          resetPayment();
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });
    } else {
      const data = {
        ...formData,
        // Ajout des montants appliqués pour chaque facture
        invoices: idsInvoicesSelected.map((invoice: any) => {
        return {
          id: invoice.id,
          amountApplied: invoice.MAEFA,
        }
      }),
        createAPaymentDifference: false,
        amount: parseFloat(parsedAmount.toFixed(2)),
      };
      updatePayment(idPayment, data)
        .then(() => {
          setCreateButton(false);
          setViewOrUpdate("");
          setFormData({});
          showToastSuccess("Encaissement mis à jour");
          setUpdatePaymentBool(!updatePaymentBool);
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });
    }
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    updatePaymentDifference(idPayment, formData)
    .then(() => {
        closeModal();
        setCreateButton(false);
        setViewOrUpdate("");
        setFormData({});
        showToastSuccess("Ecart de réglement mis à jour");
        setUpdatePaymentBool(!updatePaymentBool);
      })
      .catch(() => {
        showToastError("Une erreur est survenue");
      });
  };

  return (
    <ModalShell
      title="Écart de réglement"
      size="lg"
      onClose={() => closeModal()}
      footer={
        tabPayment === "PaymentDifferences" ? (
          <ModalButton
            variant="primary"
            type="submit"
            onClick={(e) => handleUpdate(e)}
          >
            Modifier
          </ModalButton>
        ) : (
          <>
            <ModalButton
              variant="secondary"
              type="submit"
              onClick={(e) => handleSubmitWithoutPaymentDiff(e)}
            >
              Ne pas créer
            </ModalButton>
            <ModalButton
              variant="primary"
              type="submit"
              onClick={(e) => handleSubmit(e)}
            >
              Créer
            </ModalButton>
          </>
        )
      }
    >
      <div className="rounded-xl bg-amber-50 px-4 py-3 text-center ring-1 ring-amber-100 ring-inset">
        <p className="font-Nunito text-sm text-amber-900">
          Il y a un écart de :{" "}
          {tabPayment === "PaymentDifferences" ? (
            <span className="font-bold tabular-nums">
              {formData?.amount?.toFixed(2)} €
            </span>
          ) : (
            <span className="font-bold tabular-nums">
              {trueAmount !== 0 ? Number(trueAmount || 0).toFixed(2) : "0.00"} €
            </span>
          )}
        </p>
        {tabPayment === "PaymentDifferences" ? null : (
          <p className="mt-1 font-Nunito text-xs text-amber-800">
            Que souhaitez vous faire de la différence entre l'encaissement et le
            solde des factures ?
          </p>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 font-Nunito sm:grid-cols-2">
        <Select
          label="Résolution"
          name="idPaymentDifferenceCategory"
          value={formData.idPaymentDifferenceCategory}
          onChange={handleSelectChange}
          fetchOptions={differenceCategories}
          className="cursor-pointer w-full"
        />
        <TextArea
          label="Commentaire"
          name="commentaryPaymentDifference"
          clasName="cursor-pointer w-full"
          value={formData.commentaryPaymentDifference || ""}
          onChange={handleChangeTextArea}
        />
      </div>
    </ModalShell>
  );
};

export default PaymentDifference;
